import { useParams } from 'react-router-dom'

import clsx from 'clsx'

import { useCompetitorDetailApi } from '@/common/api/modules/competitor'
import { currencyText } from '@/common/app/currency'
import { useSiteId } from '@/common/app/site'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'
import { circledNumber } from '@/common/util/number'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import BarChart from '@/components/view/atoms/chart/BarChart'
import BubbleChart, { BubbleChartItem } from '@/components/view/atoms/chart/BubbleChart'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import MultiLineChart from '@/components/view/atoms/chart/MultiLineChart'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import PointText from '@/components/view/atoms/text/PointText'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import MetricItem from '@/components/view/molecules/metric/MetricItem'
import RankChange from '@/components/view/molecules/score/RankChange'

import AnalysisActionList from '@/components/container/modules/media/action/AnalysisActionList'

import MediaAnalysisResultPageBase from './MediaAnalysisResultPageBase'

// ---- result_data の型（competitor/analyze の formatted 出力） ----
interface DomainSummary {
  domain: string
  estimated_traffic: number
  keyword_count: number
  top10_count: number
  domain_authority: number
  industry: string
}
interface KeywordRanking {
  rank?: number
  rank_change?: number | null
  rank_date?: string
}
interface KeywordItem {
  keyword: string
  search_volume: number
  ad_competition: number
  match_count: number
  rankings: Record<string, KeywordRanking>
}
interface Swot {
  strengths?: string[]
  weaknesses?: string[]
  opportunities?: string[]
  threats?: string[]
}
interface ActionItem {
  priority?: string
  action?: string
  expected_impact?: string
  target_keywords?: string[]
}
interface AdKeyword {
  keyword: string
  estimated_cpc?: number
  ad_density?: number
  ad_text_tendency?: string
  recommended_channel?: string
  channel_reason?: string
}
interface AdAnalysis {
  summary?: { high_cpc_count?: number; opportunity_count?: number; avg_cpc?: number }
  keywords?: AdKeyword[]
}
interface SearchTrend {
  keyword: string
  monthly_data?: { month: string; estimated_volume: number }[]
}
interface IndustryTrend {
  keyword: string
  estimated_monthly_volume?: number
  trend_direction?: string // rising / surging / emerging
  trend_momentum?: number
  relevance_score?: number // 0.00-1.00
  recommended_action?: string
  related_existing_keywords?: string[]
}
interface SurveyResultData {
  target_domain?: string
  competitor_domains?: string[]
  summaries?: DomainSummary[]
  keywords?: KeywordItem[]
  swot?: Swot | null
  actions?: ActionItem[]
  ad_analysis?: AdAnalysis
  search_trends?: SearchTrend[]
  industry_trends?: IndustryTrend[]
  error?: string
}

const trendBadge = (
  direction?: string
): {
  iconType: IconType
  color: IconColor
  textColor: TextColor
  label: string
} => {
  switch (direction) {
    case 'surging':
      return {
        iconType: IconType.INCREASE,
        color: IconColor.CRITICAL,
        textColor: TextColor.CRITICAL,
        label: '急上昇'
      }
    case 'rising':
      return {
        iconType: IconType.TRENDING_UP,
        color: IconColor.CAUTION,
        textColor: TextColor.CAUTION,
        label: '上昇'
      }
    case 'emerging':
      return {
        iconType: IconType.ZAP,
        color: IconColor.INFO,
        textColor: TextColor.INFO,
        label: 'NEW'
      }
    default:
      return {
        iconType: IconType.ARROW_FORWARD,
        color: IconColor.PRIMARY,
        textColor: TextColor.PRIMARY,
        label: direction || '-'
      }
  }
}

const channelLabel = (channel?: string) =>
  channel === 'ad' ? '広告' : channel === 'organic' ? '記事' : channel === 'both' ? '両方' : '-'

// 広告 vs オーガニック推奨マトリクスの系列（推奨チャネル）定義
const AD_CHANNELS: { value: string; label: string; color: string }[] = [
  { value: 'ad', label: '広告', color: '#6ECBFA' },
  { value: 'organic', label: '記事', color: '#0A8E7C' },
  { value: 'both', label: '両方', color: '#FFB800' }
]
const channelColor = (channel?: string) =>
  AD_CHANNELS.find((c) => c.value === channel)?.color ?? '#94A3B8'

const swotBackgroundColor = (textColor: TextColor) => {
  switch (textColor) {
    case TextColor.EMPHASIS:
      return 'bg-emphasis'
    case TextColor.SUCCESS:
      return 'bg-success'
    case TextColor.CAUTION:
      return 'bg-caution'
    case TextColor.CRITICAL:
      return 'bg-critical'
    case TextColor.SECONDARY:
      return 'bg-secondary'
    default:
      return 'bg-white'
  }
}

const SwotCard = ({
  title,
  items,
  color
}: {
  title: string
  items?: string[]
  color: TextColor
}) => (
  <div
    className={clsx(
      'bg-surface-secondary flex flex-col items-center gap-2 p-4',
      swotBackgroundColor(color)
    )}
  >
    <Text size={14} weight='bold' color={color}>
      {title}
    </Text>
    {items && items.length > 0 ? (
      <InsideArea border>
        <PointText size={12} color={TextColor.AI} texts={items} />
      </InsideArea>
    ) : (
      <Text color={TextColor.SECONDARY}>-</Text>
    )}
  </div>
)

const domainCardIndexColor = (index: number): TextColor => {
  switch (index) {
    case 0:
      return TextColor.SORA
    case 1:
      return TextColor.ORANGE
    case 2:
      return TextColor.CAUTION
    case 3:
      return TextColor.PURPLE
    default:
      return TextColor.SECONDARY
  }
}

const DomainCard = ({
  summary,
  isTarget,
  index
}: {
  summary: DomainSummary
  isTarget: boolean
  index: number
}) => (
  <Area borderColor={index === 0 ? 'sora' : undefined}>
    <Heading subText={isTarget ? '自社ドメイン' : undefined}>{summary.domain}</Heading>
    <AreaContent>
      <ItemAlign>
        <Text size={12} weight='bold'>
          推定月間流入数
        </Text>
        <Text size={24} weight='bold' color={domainCardIndexColor(index)}>
          {(summary.estimated_traffic ?? 0).toLocaleString()}
        </Text>
      </ItemAlign>
      <div className='flex flex-wrap gap-x-4 gap-y-2'>
        <MetricItem label='キーワード数' value={(summary.keyword_count ?? 0).toLocaleString()} />
        <MetricItem label='10位以内' value={(summary.top10_count ?? 0).toLocaleString()} />
        <MetricItem
          label='DA'
          value={summary.domain_authority ?? 0} // DAは0-100の数値で、数値が高いほどドメイン全体のSEO評価が高いことを示す指標
        />
        {summary.industry && <MetricItem label='業界' value={summary.industry} />}
      </div>
    </AreaContent>
  </Area>
)

// 競合分析結果（competitor：AI推定の自社×競合SWOT・KW順位・業界トレンド）
const MediaAnalysisCompetitorResultPage = () => {
  const { historyId } = useParams()
  const siteId = useSiteId()
  // 分析中（status=analyzing）の間は2秒間隔でポーリングし、完了したら自動的に停止する
  const { data, error } = useCompetitorDetailApi(
    siteId && historyId ? { site_id: siteId, history_id: Number(historyId) } : undefined,
    { refreshInterval: (latest) => (latest?.status === 'analyzing' ? 2000 : 0) }
  )

  const result = (data?.result_data ?? {}) as SurveyResultData
  const input = (data?.input_data ?? {}) as { target_domain?: string }

  const targetDomain = result.target_domain || input.target_domain || ''
  const summaries = result.summaries ?? []
  const keywords = result.keywords ?? []
  const domains = summaries.length
    ? summaries.map((s) => s.domain)
    : [targetDomain, ...(result.competitor_domains ?? [])].filter(Boolean)
  const adAnalysis = result.ad_analysis
  const adKeywords = adAnalysis?.keywords ?? []
  // 広告 vs オーガニック推奨マトリクス（X=CPC, Y=広告密度%, 色=推奨チャネル）
  const adBubbleItems: BubbleChartItem[] = adKeywords.map((kw) => ({
    x: kw.estimated_cpc ?? 0,
    y: Math.round((kw.ad_density ?? 0) * 100),
    r: 7,
    label: kw.keyword,
    color: channelColor(kw.recommended_channel)
  }))
  const trends = result.search_trends ?? []
  const industryTrends = result.industry_trends ?? []

  // 検索トレンドの月軸（昇順ユニーク）と系列（上位6KW）
  const months = Array.from(
    new Set(trends.flatMap((t) => (t.monthly_data ?? []).map((m) => m.month)))
  ).sort()
  const trendItems = trends.slice(0, 6).map((t) => ({
    title: t.keyword,
    values: months.map((m) => {
      const md = (t.monthly_data ?? []).find((x) => x.month === m)
      return md ? md.estimated_volume : null
    })
  }))

  return (
    <MediaAnalysisResultPageBase
      type={MediaAnalysisType.COMPETITOR}
      data={data}
      error={error}
      targetDomain={targetDomain}
      resultError={result.error}
    >
      {/* ドメイン比較 */}
      {summaries.length > 0 && (
        <div className='grid grid-cols-1 gap-4 pb-6 sm:grid-cols-2 lg:grid-cols-4'>
          {summaries.map((s, index) => (
            <DomainCard
              key={s.domain}
              summary={s}
              isTarget={s.domain === targetDomain}
              index={index}
            />
          ))}
        </div>
      )}
      {/* 推奨アクション */}
      <AnalysisActionList siteId={siteId} mediaAnalysisId={Number(historyId)} />

      {/* 業界トレンドKW（新興キーワード） */}
      {industryTrends.length > 0 && (
        <Area bottomMargin={16}>
          <Heading>業界トレンドキーワード</Heading>
          <table className='w-full'>
            <thead>
              <TableRow head noBorder>
                <TableHeadItem type='thin'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    キーワード
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={128}>
                  <Text size={12} color={TextColor.SECONDARY}>
                    推定月間検索数
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={88}>
                  <Text size={12} color={TextColor.SECONDARY}>
                    トレンド
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={128}>
                  <Text size={12} color={TextColor.SECONDARY}>
                    関連度
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={160}>
                  <Text size={12} color={TextColor.SECONDARY}>
                    推奨アクション
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={188}>
                  <Text size={12} color={TextColor.SECONDARY}>
                    関連既存キーワード
                  </Text>
                </TableHeadItem>
              </TableRow>
            </thead>
            <tbody>
              {industryTrends.map((t, i) => {
                const badge = trendBadge(t.trend_direction)
                const relevance = Math.round((t.relevance_score ?? 0) * 100)
                return (
                  <TableRow key={i}>
                    <TableRowItem>
                      <Badge size={12} badgeType={BadgeType.KEYWORD}>
                        {t.keyword}
                      </Badge>
                    </TableRowItem>
                    <TableRowItem width={128}>
                      <Text>{(t.estimated_monthly_volume ?? 0).toLocaleString()}</Text>
                    </TableRowItem>
                    <TableRowItem width={88}>
                      <IconText
                        direction='horizon'
                        iconType={badge.iconType}
                        color={badge.color}
                        textColor={badge.textColor}
                        text={badge.label}
                      />
                    </TableRowItem>
                    <TableRowItem width={128}>
                      <ItemAlign gapX={4} align='center'>
                        <div className='bg-fill-tertiary h-1.5 w-12 overflow-hidden rounded-full'>
                          <div
                            className='bg-fill-primary-brand h-full rounded-full'
                            style={{ width: `${relevance}%` }}
                          />
                        </div>
                        <Text size={12}>{relevance}%</Text>
                      </ItemAlign>
                    </TableRowItem>
                    <TableRowItem width={160}>
                      <Badge badgeType={BadgeType.SQUARE_PRIMARY}>
                        {t.recommended_action || '-'}
                      </Badge>
                    </TableRowItem>
                    <TableRowItem width={188} innerGap={4}>
                      {(t.related_existing_keywords ?? []).map((kw, ki) => (
                        <Badge key={ki} size={10} badgeType={BadgeType.KEYWORD}>
                          {kw}
                        </Badge>
                      ))}
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </tbody>
          </table>
        </Area>
      )}

      {/* キーワードランキング */}
      {keywords.length > 0 && domains.length > 0 && (
        <Area bottomMargin={16}>
          <Heading>キーワードランキング</Heading>
          <table className='w-full'>
            <thead>
              <TableRow head noBorder>
                <TableHeadItem type='thin'>
                  <Text size={12}>自社順位</Text>
                </TableHeadItem>
                <TableHeadItem type='thin'>
                  <Text size={12}>キーワード</Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={64}>
                  <Text size={12}>一致数</Text>
                </TableHeadItem>
                {domains.map((d, index) => (
                  <TableHeadItem key={d} type='thin' width={128}>
                    <Text size={12} truncate>
                      {`競合${circledNumber(index + 1)}`}
                      <br />
                      {d}
                    </Text>
                  </TableHeadItem>
                ))}
                <TableHeadItem type='thin' width={128}>
                  <Text size={12}>月間検索数</Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={128}>
                  <Text size={12}>広告出稿度</Text>
                </TableHeadItem>
              </TableRow>
            </thead>
            <tbody>
              {keywords.map((kw, index) => (
                <TableRow key={index}>
                  <TableRowItem width={88}>
                    <Badge badgeType={BadgeType.SQUARE_CAUTION}>
                      <ItemAlign gapX={2} align='center' direction='horizon'>
                        <Icon type={IconType.CROWN} color={IconColor.CAUTION} />
                        <Text color={TextColor.CAUTION} weight={'bold'}>
                          {index + 1}
                        </Text>
                      </ItemAlign>
                    </Badge>
                  </TableRowItem>
                  <TableRowItem width={188}>
                    <Badge badgeType={BadgeType.KEYWORD}>{kw.keyword}</Badge>
                  </TableRowItem>
                  <TableRowItem width={40}>
                    <Text>{kw.match_count ?? 0}</Text>
                  </TableRowItem>
                  {domains.map((d) => {
                    const r = kw.rankings?.[d]
                    return (
                      <TableRowItem key={d} width={128}>
                        {r?.rank != null ? (
                          <Badge badgeType={BadgeType.KEYWORD} size={10}>
                            {r.rank}
                          </Badge>
                        ) : (
                          <Text color={TextColor.SECONDARY}>-</Text>
                        )}
                      </TableRowItem>
                    )
                  })}
                  <TableRowItem width={128}>
                    <Text>{(kw.search_volume ?? 0).toLocaleString()}</Text>
                  </TableRowItem>
                  <TableRowItem width={128}>
                    <ItemAlign gapX={4} align='center'>
                      <div className='bg-fill-tertiary h-1.5 w-12 overflow-hidden rounded-full'>
                        <div
                          className='bg-fill-primary-brand h-full rounded-full'
                          style={{
                            width: `${Math.min(Math.round((kw.ad_competition ?? 0) * 100), 100)}%`
                          }}
                        />
                      </div>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {Math.round((kw.ad_competition ?? 0) * 100)}%
                      </Text>
                    </ItemAlign>
                  </TableRowItem>
                </TableRow>
              ))}
            </tbody>
          </table>
        </Area>
      )}

      {/* 想定コンテンツ特性（検索トレンド） */}
      {trendItems.length > 0 && months.length > 0 && (
        <Area bottomMargin={16}>
          <ChartWrapper>
            <ChartTitle>検索トレンド時系列グラフ</ChartTitle>
            <MultiLineChart labels={months} items={trendItems} />
          </ChartWrapper>
        </Area>
      )}

      {/* 広告分析特性 */}
      {adAnalysis && (adAnalysis.summary || adKeywords.length > 0) && (
        <Area bottomMargin={16}>
          <Heading>広告出稿分析</Heading>
          <AreaContent>
            {adAnalysis.summary && (
              <SummaryItemList shadow>
                <SummaryItem color={'secondary'}>
                  <Text weight='bold'>高CPC領域</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {adAnalysis.summary.high_cpc_count ?? 0}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem color={'secondary'}>
                  <Text weight='bold'>広告手薄な狙い目</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {adAnalysis.summary.opportunity_count ?? 0}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem color={'secondary'}>
                  <Text weight='bold'>業界平均CPC</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {currencyText(adAnalysis.summary.avg_cpc ?? 0)}
                    </Text>
                  </ItemAlign>
                </SummaryItem>
              </SummaryItemList>
            )}
          </AreaContent>
          <Line />
          {adKeywords.length > 0 && (
            <div className='flex flex-col gap-4 lg:flex-row'>
              {/* 広告 vs オーガニック推奨マトリクス（X=CPC, Y=広告密度, 色=推奨チャネル） */}
              <div className='lg:w-[440px] lg:shrink-0'>
                <ChartWrapper>
                  <ChartTitle>広告 vs オーガニック推奨マトリクス</ChartTitle>
                  <div className='absolute top-3 right-4 z-10 flex gap-3'>
                    {AD_CHANNELS.map((c) => (
                      <ItemAlign key={c.value} gapX={4} align='center'>
                        <span
                          className='inline-block size-2 rounded-full'
                          style={{ backgroundColor: c.color }}
                        />
                        <Text size={12}>{c.label}</Text>
                      </ItemAlign>
                    ))}
                  </div>
                  <BubbleChart
                    items={adBubbleItems}
                    xTitle='CPC（¥）'
                    yTitle='広告密度（%）'
                    yMax={100}
                    showLabels={false}
                    tooltipLines={(item) => [
                      `CPC: ¥${item.x.toLocaleString()}`,
                      `広告密度: ${item.y}%`
                    ]}
                  />
                </ChartWrapper>
              </div>
              {/* キーワード別テーブル */}
              <div className='min-w-0 flex-1 overflow-x-auto'>
                <table className='border-secondary w-full border-l'>
                  <thead>
                    <TableRow head noBorder>
                      <TableHeadItem type='thin'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          キーワード
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin' width={88}>
                        <Text size={12} color={TextColor.SECONDARY}>
                          CPC
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin' width={88}>
                        <Text size={12} color={TextColor.SECONDARY}>
                          出稿密度
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin' width={88}>
                        <Text size={12} color={TextColor.SECONDARY}>
                          推奨
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin' width={160}>
                        <Text size={12} color={TextColor.SECONDARY}>
                          理由
                        </Text>
                      </TableHeadItem>
                    </TableRow>
                  </thead>
                  <tbody>
                    {adKeywords.map((kw, i) => (
                      <TableRow key={i}>
                        <TableRowItem>
                          <Badge badgeType={BadgeType.KEYWORD}>{kw.keyword}</Badge>
                        </TableRowItem>
                        <TableRowItem width={88}>
                          <Text>{currencyText(kw.estimated_cpc ?? 0)}</Text>
                        </TableRowItem>
                        <TableRowItem width={88} align='middle'>
                          <Text>{Math.round((kw.ad_density ?? 0) * 100)}%</Text>
                        </TableRowItem>
                        <TableRowItem width={88} align='middle'>
                          <Badge size={10} badgeType={BadgeType.SQUARE_SECONDARY}>
                            {channelLabel(kw.recommended_channel)}
                          </Badge>
                        </TableRowItem>
                        <TableRowItem width={160}>
                          <Text size={12}>{kw.ad_text_tendency || '-'}</Text>
                        </TableRowItem>
                      </TableRow>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Area>
      )}
      {/* 概要グラフ */}
      {summaries.length > 0 && (
        <Area bottomMargin={16}>
          <Heading>比較グラフ</Heading>
          <div className='flex gap-6'>
            <div className='lg:w-[calc(50%-0.75rem)]'>
              <ChartWrapper>
                <ChartTitle>推定流入数</ChartTitle>
                <BarChart
                  labels={domains}
                  items={summaries.map((s) => ({
                    title: s.domain,
                    values: [s.estimated_traffic ?? 0]
                  }))}
                  single
                />
              </ChartWrapper>
            </div>
            <div className='lg:w-[calc(50%-0.75rem)]'>
              <ChartWrapper>
                <ChartTitle>ユニークキーワード数</ChartTitle>
                <BarChart
                  labels={domains}
                  items={summaries.map((s) => ({
                    title: s.domain,
                    values: [s.keyword_count ?? 0]
                  }))}
                  single
                />
              </ChartWrapper>
            </div>
          </div>
        </Area>
      )}
      {/* SWOT分析 */}
      {result.swot && (
        <Area bottomMargin={16}>
          <Heading>SWOT分析</Heading>
          <AreaContent>
            <table className='bg-surface rounded-xl border-white'>
              <thead className='border-collapse border-4 border-white'>
                <tr className='bg-secondary overflow-hidden rounded-t-xl border-4 border-white'>
                  <th />
                  <th className='border-4 border-white p-1'>
                    <Text>プラス要因</Text>
                  </th>
                  <th className='border-4 border-white p-1'>
                    <Text>マイナス要因</Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className='border-collapse border-4 border-white'>
                  <th className='bg-secondary max-w-7.5 border-4 border-white p-1'>
                    <Text>内部環境</Text>
                  </th>
                  <th className='border-4 border-white'>
                    <SwotCard
                      title='Strengths：強み'
                      items={result.swot.strengths}
                      color={TextColor.EMPHASIS}
                    />
                  </th>
                  <th className='border-4 border-white'>
                    <SwotCard
                      title='Weaknesses：弱み'
                      items={result.swot.weaknesses}
                      color={TextColor.CRITICAL}
                    />
                  </th>
                </tr>
                <tr>
                  <th className='bg-secondary max-w-7.5 border-4 border-white p-1'>
                    <Text>外部環境</Text>
                  </th>
                  <th className='border-4 border-white'>
                    <SwotCard
                      title='Opportunities：機会'
                      items={result.swot.opportunities}
                      color={TextColor.SUCCESS}
                    />
                  </th>
                  <th>
                    <SwotCard
                      title='Threats：脅威'
                      items={result.swot.threats}
                      color={TextColor.CAUTION}
                    />
                  </th>
                </tr>
              </tbody>
            </table>
          </AreaContent>
        </Area>
      )}
    </MediaAnalysisResultPageBase>
  )
}

export default MediaAnalysisCompetitorResultPage
